import React, { useMemo } from "react";
import {
  Bar,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatNumber = (value, fractionDigits = 2) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

const TooltipCard = ({ active, payload, metricLabelMap, title }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        minWidth: "180px",
        borderRadius: "12px",
        border: "1px solid rgba(36, 87, 191, 0.16)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,247,255,0.98) 100%)",
        boxShadow: "0 14px 30px rgba(15,23,42,0.16)",
        padding: "10px 12px",
        backdropFilter: "blur(8px)",
        transform: "translateY(0px)",
        transition: "all 180ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div
        style={{
          fontSize: "10px",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#2457BF",
          fontWeight: 700,
          marginBottom: "8px",
        }}
      >
        {title}
      </div>
      {payload.map((entry) => {
        const dataKey = String(entry?.dataKey || "");
        const label = metricLabelMap[dataKey] || dataKey;
        const fractionDigits =
          dataKey === "macd" || dataKey === "signal" ? 4 : 2;
        const valueText =
          dataKey === "volume"
            ? Number(entry?.value || 0).toLocaleString("en-IN")
            : formatNumber(entry?.value, fractionDigits);

        return (
          <div
            key={dataKey}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginTop: "6px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: "#475569",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "999px",
                  backgroundColor: entry?.color || "#57C3DD",
                  boxShadow: `0 0 0 2px ${entry?.color || "#57C3DD"}22`,
                }}
              />
              {label}
            </span>
            <span style={{ color: "#0F172A", fontSize: "12px", fontWeight: 700 }}>
              {valueText}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const POINTS_BY_RANGE = {
  "1D": 180,
  "1W": 220,
  "1M": 320,
};

const seededNoise = (seed, index) => {
  const value = Math.sin(seed * 17.193 + index * 57.283) * 43758.5453;
  return value - Math.floor(value);
};

const smoothWalk = (base, seed, points, drift, vol) => {
  let prev = base;
  return Array.from({ length: points }, (_, idx) => {
    const noise = seededNoise(seed, idx) - 0.5;
    const wave = Math.sin((idx / Math.max(points - 1, 1)) * Math.PI * 4.8) * vol * 0.45;
    const move = drift + noise * vol * 0.65 + wave;
    prev = Math.max(5, prev * (1 + move));
    return prev;
  });
};

const movingAverage = (list, period) =>
  list.map((_, idx) => {
    if (idx < period - 1) return list[idx];
    const slice = list.slice(idx - period + 1, idx + 1);
    return slice.reduce((sum, val) => sum + val, 0) / slice.length;
  });

const buildSeries = ({ basePrice, seed, trendBias, range }) => {
  const points = POINTS_BY_RANGE[range] || POINTS_BY_RANGE["1D"];
  const price = smoothWalk(
    basePrice * 0.9,
    seed + 4,
    points,
    0.00065 * (trendBias >= 0 ? 1 : -1),
    0.011,
  );
  const ma = movingAverage(price, 18);
  const oscFast = movingAverage(price, 8);
  const oscSlow = movingAverage(price, 21);

  return Array.from({ length: points }, (_, index) => ({
    idx: index,
    price: Number(price[index].toFixed(3)),
    ma: Number(ma[index].toFixed(3)),
    volume: Math.max(
      200,
      Math.round(
        1800 +
          Math.abs(price[index] - (price[index - 1] || price[index])) * 5200 +
          (seededNoise(seed + 20, index) * 1600),
      ),
    ),
    macd: Number((oscFast[index] - oscSlow[index]).toFixed(4)),
    signal: Number(
      (movingAverage(oscFast.map((v, i) => v - oscSlow[i]), 9)[index] || 0).toFixed(4),
    ),
    rsi: Number(
      (
        50 +
        Math.sin(index * 0.18 + seed) * 28 +
        (seededNoise(seed + 200, index) - 0.5) * 22
      ).toFixed(2),
    ),
  }));
};

const SubchartsStockLines = ({
  basePrice = 1000,
  seed = 1,
  trendBias = 1,
  range = "1D",
}) => {
  const data = useMemo(
    () => buildSeries({ basePrice, seed, trendBias, range }),
    [basePrice, seed, trendBias, range],
  );

  return (
    <div style={{ width: "100%", height: "100%", display: "grid", gridTemplateRows: "2.1fr 1.1fr 0.9fr", gap: "8px" }}>
      <div style={{ minHeight: 0, background: "#f2f3f5", borderRadius: "8px", border: "1px solid #D5DDE8" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="idx" hide />
            <YAxis yAxisId="price" orientation="right" tick={{ fill: "#444", fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="vol" hide />
            <Tooltip
              cursor={{ stroke: "#2457BF", strokeOpacity: 0.35, strokeDasharray: "4 4" }}
              isAnimationActive
              animationDuration={260}
              animationEasing="ease-out"
              wrapperStyle={{ transition: "transform 180ms ease, opacity 180ms ease" }}
              content={
                <TooltipCard
                  title="Price Snapshot"
                  metricLabelMap={{ price: "Price", ma: "MA", volume: "Volume" }}
                />
              }
            />
            <Bar yAxisId="vol" dataKey="volume" fill="#7cc7d7" opacity={0.75} barSize={2} />
            <Line yAxisId="price" type="monotone" dataKey="price" stroke="#57C3DD" strokeWidth={1.6} dot={false} />
            <Line yAxisId="price" type="monotone" dataKey="ma" stroke="#EC1581" strokeWidth={1.8} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div style={{ minHeight: 0, background: "#f2f3f5", borderRadius: "8px", border: "1px dashed #B9C3D2" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="idx" hide />
            <YAxis orientation="right" tick={{ fill: "#444", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ stroke: "#2457BF", strokeOpacity: 0.3, strokeDasharray: "4 4" }}
              isAnimationActive
              animationDuration={240}
              animationEasing="ease-out"
              wrapperStyle={{ transition: "transform 180ms ease, opacity 180ms ease" }}
              content={
                <TooltipCard
                  title="Momentum"
                  metricLabelMap={{ macd: "MACD", signal: "Signal" }}
                />
              }
            />
            <Bar
              dataKey="macd"
              fill="#7BC2B4"
              barSize={2}
              opacity={0.85}
            />
            <Line type="monotone" dataKey="macd" stroke="#62BFB1" strokeWidth={1.6} dot={false} />
            <Line type="monotone" dataKey="signal" stroke="#E98977" strokeWidth={1.6} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div style={{ minHeight: 0, background: "#f2f3f5", borderRadius: "8px", border: "1px dashed #B9C3D2" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="idx"
              tickFormatter={(value) => {
                if (range === "1D") return "";
                if (range === "1W") return 2000 + Math.floor(value / 20);
                return 1999 + Math.floor(value / 30);
              }}
              tick={{ fill: "#444", fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              orientation="right"
              tick={{ fill: "#444", fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ stroke: "#2457BF", strokeOpacity: 0.3, strokeDasharray: "4 4" }}
              isAnimationActive
              animationDuration={240}
              animationEasing="ease-out"
              wrapperStyle={{ transition: "transform 180ms ease, opacity 180ms ease" }}
              content={<TooltipCard title="Strength" metricLabelMap={{ rsi: "RSI" }} />}
            />
            <Line type="monotone" dataKey="rsi" stroke="#57C3DD" strokeWidth={1.8} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SubchartsStockLines;
